import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Modal,
  Tooltip,
} from "antd";
import ReactApexChart from "react-apexcharts";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  CalendarOutlined,
  PercentageOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import { useGetFormMetricsQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";
import AlertPopup from "../FMSDashboardPopups/AlertPopup";
import dayjs from "dayjs";

const { Text } = Typography;

// Enhanced color scheme
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
  danger: "#dc2626",
};

export default function FSMFormMetricsDashboard({ fromDate, toDate }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedChart, setExpandedChart] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const { data: metrics, isLoading, error } = useGetFormMetricsQuery({ from: fromDate && fromDate.format("YYYY-MM-DDTHH:mm:ss"), to: toDate && toDate.format("YYYY-MM-DDTHH:mm:ss") },{refetchOnMountOrArgChange: true});

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Text type="danger">{error.message}</Text>
      </Card>
    );

  const {
    overview = {},
    timeliness = {},
    technicianPerformance = [],
    formsByTaskType = [],
    backlogTrend = [],
    locationAnalysis = [],
  } = metrics || {};

  const { totalForms = 0, submittedForms = 0, pendingForms = 0, todoForms = 0, completionRate = 0 } = overview;
  // SLA and timeliness data
  const {
    averageSubmissionDelay = 0,
    slaCompliance = 0,
    withinSLA = 0,
    total = 0,
  } = timeliness;

  // Status cards configuration
  const statusCards = [
    {
      title: trans("Total Forms"),
      icon: <FileTextOutlined />,
      count: totalForms,
      color: colorScheme.info,
      tooltip: trans("Total number of submitted and pending forms"),
      api: "useGetFormMetricsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Form Name"), dataIndex: "formName", width: 200 },
        {
          title: trans("Created By"),
          dataIndex: "createdByName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Date"), dataIndex: "taskStartDate", width: 150 ,render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "- - - - -"},
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedToName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Task"), dataIndex: "taskName", width: 150 },
        { title: trans("POI"), dataIndex: "poiName", width: 180, render: (value) => value || "- - - - -", },
        { title: trans("Status"), dataIndex: "formStatus", width: 100 },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "all",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("Submitted"),
      icon: <CheckCircleOutlined />,
      count: submittedForms,
      color: colorScheme.success,
      tooltip: trans("Forms successfully submitted"),
            api: "useGetFormMetricsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Form Name"), dataIndex: "formName", width: 200 },
        {
          title: trans("Created By"),
          dataIndex: "createdByName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Date"), dataIndex: "taskStartDate", width: 150 ,render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "- - - - -"},
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedToName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Task"), dataIndex: "taskName", width: 150 },
        { title: trans("POI"), dataIndex: "poiName", width: 180, render: (value) => value || "- - - - -", },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "submitted",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("Pending"),
      icon: <ClockCircleOutlined />,
      count: pendingForms,
      color:
        pendingForms > totalForms * 0.3
          ? colorScheme.danger
          : colorScheme.warning,
      tooltip: trans("Forms awaiting review or approval"),
      api: "useGetFormMetricsQuery",
      extractData: (response) => response?.data || [],
      frontendFilter: null,
      columns: [
        { title: trans("Form Name"), dataIndex: "formName", width: 200 },
        {
          title: trans("Created By"),
          dataIndex: "createdByName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Date"), dataIndex: "taskStartDate", width: 150 ,render: (value) => value ? dayjs(value).format("DD-MM-YYYY") : "- - - - -"},
        {
          title: trans("Assigned Field Staff"),
          dataIndex: "assignedToName",
          width: 200,
          render: (value) => value || "- - - - -",
        },
        { title: trans("Task"), dataIndex: "taskName", width: 150 },
        { title: trans("POI"), dataIndex: "poiName", width: 180, render: (value) => value || "- - - - -", },
      ],
      query: {
        page: 1,
        limit: 10,
        filterBy: "pending",
        from: fromDate ? fromDate.format("YYYY-MM-DDTHH:mm:ss") : null,
        to: toDate ? toDate.format("YYYY-MM-DDTHH:mm:ss") : null,
      },
      extractTotal: (response) => response?.totalResults || 0,
    },
    {
      title: trans("Todo"),
      icon: <ClockCircleOutlined />,
      count: todoForms,
      color: colorScheme.neutral,
      tooltip: trans("Forms for tasks not yet completed"),
    },
    {
      title: trans("Completion Rate"),
      icon: <PercentageOutlined />,
      count: `${completionRate}%`,
      color:
        completionRate >= 90
          ? colorScheme.success
          : completionRate >= 70
          ? colorScheme.warning
          : colorScheme.danger,
      tooltip: "Percentage of forms completed successfully",
    },
  ];

  // Prepare task type bar chart data for ApexCharts
  const taskTypeData = formsByTaskType.slice(0, 6).map((item) => ({
    taskType: `${item.taskType} (${item.priority})`,
    submitted: parseInt(item.formsSubmitted),
    pending: parseInt(item.formsPending),
    completionRate: parseFloat(item.completionRate),
  }));

  // Prepare backlog trend line chart data for ApexCharts
  const backlogCategories = backlogTrend.map((item) => item.date);
  const backlogPendingData = backlogTrend.map((item) => item.pendingForms);
  const backlogCompletedData = backlogTrend.map((item) => item.completedForms);

  // Prepare location performance data for ApexCharts
  const locationData = locationAnalysis.slice(0, 8).map((item) => ({
    location: item.location,
    totalForms: parseInt(item.totalForms),
    completed: parseInt(item.formsCompleted),
    pending: parseInt(item.totalForms) - parseInt(item.formsCompleted),
    completionRate: parseFloat(item.completionRate),
  }));

  // ApexCharts configurations
  const taskTypeBarOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: [colorScheme.success, colorScheme.warning],
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: "60%",
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "10px",
        colors: [colorScheme.dark],
      },
    },
    xaxis: {
      categories: taskTypeData.map((item) => item.taskType),
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
        rotate: -45,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "11px",
        },
      },
      title: {
        text: trans("Forms"),
        style: {
          color: colorScheme.dark,
          fontSize: "12px",
          fontWeight: "600",
        },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: "500",
      labels: {
        colors: colorScheme.dark,
      },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: function (val) {
          return val + " " + trans("forms");
        },
      },
    },
  };

  // Location Performance Heatmap for expanded view
  const locationHeatmapOptions = {
    chart: {
      type: "heatmap",
      fontFamily: "'Cairo', sans-serif !important",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: ["#008FFB"],
    dataLabels: {
      enabled: true,
      style: {
        colors: ["#fff"],
        fontSize: "12px",
        fontWeight: "600",
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    title: {
      text: "",
    },
    grid: {
      padding: {
        right: 20,
      },
    },
    xaxis: {
      type: "category",
      categories: [
        trans("Completion Rate"),
        trans("Total Forms"),
        trans("Forms Completed"),
      ],
      labels: {
        style: {
          colors: colorScheme.dark,
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colorScheme.dark,
          fontSize: "11px",
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (val, opts) {
          const metric = opts.w.config.xaxis.categories[opts.dataPointIndex];
          if (metric === trans("Completion Rate")) {
            return val + "%";
          } else {
            return (
              val +
              " " +
              (metric === trans("Total Forms")
                ? trans("forms")
                : trans("tasks"))
            );
          }
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 0,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: [
            {
              from: 0,
              to: 25,
              name: trans("Low"),
              color: colorScheme.danger,
            },
            {
              from: 26,
              to: 51,
              name: trans("Fair"),
              color: colorScheme.warning,
            },
            {
              from: 51,
              to: 75,
              name: trans("Good"),
              color: colorScheme.info,
            },
            {
              from: 76,
              to: 100,
              name: trans("Excellent"),
              color: colorScheme.success,
            },
          ],
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      labels: {
        colors: colorScheme.dark,
      },
    },
  };

  // Prepare heatmap data from actual locationAnalysis
  const heatmapData = locationAnalysis.slice(0, 8).map((location, index) => {
    return {
      name: location.location,
      data: [
        {
          x: trans("Completion Rate"),
          y: parseFloat(location.completionRate) || 0,
        },
        {
          x: trans("Total Forms"),
          y: parseInt(location.totalForms) || 0,
        },
        {
          x: trans("Forms Completed"),
          y: parseInt(location.formsCompleted) || 0,
        },
      ],
    };
  });

  // Expanded Chart Modal Component
  const ExpandedChartModal = () => {
    return (
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            {expandedChart === "backlog" && (
              <>
                <CalendarOutlined
                  style={{
                    fontSize: "16px",
                    color: colorScheme.primary,
                    marginInlineEnd: "8px",
                  }}
                />
                {trans("7-Day Backlog Trend - Expanded View")}
              </>
            )}
            {expandedChart === "location" && (
              <>
                <EnvironmentOutlined
                  style={{
                    fontSize: "16px",
                    color: colorScheme.primary,
                    marginInlineEnd: "8px",
                  }}
                />
                {trans("Performance by Location - Expanded View")}
              </>
            )}
          </div>
        }
        open={expandedChart !== null}
        onCancel={() => setExpandedChart(null)}
        width="90%"
        style={{ top: 20 }}
        footer={null}
        bodyStyle={{ padding: "20px" }}
      >
        {expandedChart === "backlog" && backlogTrend.length > 0 && (
          <div style={{ height: "500px" }}>
            <ReactApexChart
              options={{
                ...backlogLineOptions,
                chart: {
                  ...backlogLineOptions.chart,
                  height: 500,
                },
              }}
              series={[
                {
                  name: trans("Pending Forms"),
                  data: backlogPendingData,
                },
                {
                  name: trans("Completed Forms"),
                  data: backlogCompletedData,
                },
              ]}
              type="area"
              height={500}
            />
          </div>
        )}

        {expandedChart === "location" && locationData.length > 0 && (
          <Row gutter={[16, 16]}>
            {/* Bar Chart */}
            <Col span={12}>
              <Card
                title={trans("Forms Distribution by Location")}
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <ReactApexChart
                  options={{
                    ...locationBarOptions,
                    chart: {
                      ...locationBarOptions.chart,
                      height: 400,
                    },
                  }}
                  series={[
                    {
                      name: trans("Total Forms"),
                      data: locationData.map((item) => item.totalForms),
                    },
                    {
                      name: trans("Completed"),
                      data: locationData.map((item) => item.completed),
                    },
                    {
                      name: trans("Pending"),
                      data: locationData.map((item) => item.pending),
                    },
                  ]}
                  type="bar"
                  height={400}
                />
              </Card>
            </Col>

            {/* Heatmap */}
            <Col span={12}>
              <Card
                title={trans("Performance Heatmap")}
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <ReactApexChart
                  options={locationHeatmapOptions}
                  series={heatmapData}
                  type="heatmap"
                  height={400}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Modal>
    );
  };

  const backlogLineOptions = {
    chart: {
      type: "area",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: [colorScheme.warning, colorScheme.success],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    markers: {
      size: 5,
      colors: [colorScheme.warning, colorScheme.success],
      strokeColors: "#fff",
      strokeWidth: 2,
    },
    xaxis: {
      categories: backlogCategories,
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "10px",
        },
        rotate: -45,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: colorScheme.neutral,
          fontSize: "11px",
        },
      },
      title: {
        text: trans("Forms"),
        style: {
          color: colorScheme.dark,
          fontSize: "12px",
          fontWeight: "600",
        },
      },
    },
    grid: {
      borderColor: colorScheme.border,
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: "500",
      labels: {
        colors: colorScheme.dark,
      },
    },
    tooltip: {
      theme: "light",
      x: { format: "dd/MM/yy" },
      y: {
        formatter: function (val) {
          return val + " " + trans("forms");
        },
      },
    },
  };

  const locationBarOptions = {
    chart: {
      type: "bar",
      fontFamily: "'Cairo', sans-serif !important",
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
        borderRadius: 4,
        borderRadiusApplication: "end",

        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: "10px",
        colors: ["#000"],
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: locationData.map((item) => item.location),
      labels: {
        rotate: -45,
        style: {
          fontSize: "10px",
          colors: "#888",
        },
      },
    },
    yaxis: {
      title: {
        text: trans("Forms"),
        style: {
          color: "#333",
          fontSize: "12px",
          fontWeight: "600",
        },
      },
      labels: {
        style: {
          colors: "#888",
          fontSize: "10px",
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: ["#8D79F6"], // end color
        inverseColors: false,
        opacityFrom: [0.6, 0.8, 1], // different opacities
        opacityTo: [0.6, 0.8, 1],
        stops: [0, 100],
      },
    },
    colors: ["#B09FFF", "#B09FFF", "#B09FFF"], // base start color for all
    tooltip: {
      theme: "light",
      style: {
        fontSize: "12px",
      },
      y: {
        formatter: function (val) {
          return val + " " + trans("Forms");
        },
      },
    },
    grid: {
      borderColor: "#eee",
      strokeDashArray: 3,
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontWeight: "500",
      labels: {
        colors: "#333",
      },
    },
  };

  return (
    <div
      style={{
        minHeight: "100%",
        padding: "16px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,
      }}
    >
      {/* Header - Matching Service Request Dashboard style */}
      <SectionTitle
        title={"Form Metrics"}
        subtitle={"Comprehensive Form Analytics"}
        imgSrc={`${process.env.PUBLIC_URL}/assets/form-metrics.svg`}
        imgBgColor="#E6F7FF"
        filterApplied={fromDate && toDate ? true : false}
        filterText={`From ${dayjs(fromDate).format("DD/MM/YYYY")} - ${dayjs(toDate).format("DD/MM/YYYY")}`}
      />

      {/* Status Cards - Enhanced styling */}
      <Row gutter={[8, 8]} style={{ marginBottom: "20px" }} wrap={false}>
        {statusCards.map((card, i) => (
          <Col flex={1} key={i} style={{ minWidth: 0 }}>
            <NumbersCard
              title={card.title}
              value={card.count}
              suffix={card.suffix}
              color={card.color}
              tooltip={card.tooltip}
              onClick={() => card.api && setSelectedForm(card)}
              popup={card.api ? true : false}
            />
          </Col>
        ))}
      </Row>

      {/* Main Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        {/* 7-Day Backlog Trend */}
        {backlogTrend.length > 0 && (
          <Col span={12}>
            <Tooltip title={trans("Weekly backlog performance trend")}>
              <Card
                style={{
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "8px" }}
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>
                        {trans("7-Day Backlog Trend")}
                      </span>
                    </div>
                    <Button
                      type="text"
                      icon={
                        <img
                          src={`${process.env.PUBLIC_URL}/assets/Light/expand.svg`}
                          alt="expend"
                        />
                      }
                      onClick={() => setExpandedChart("backlog")}
                      style={{
                        color: colorScheme.primary,
                        padding: "4px 8px",
                        border: "1px solid #EBECF0",
                      }}
                      size="small"
                    />
                  </div>
                }
              >
                <ReactApexChart
                  options={backlogLineOptions}
                  series={[
                    {
                      name: trans("Pending Forms"),
                      data: backlogPendingData,
                    },
                    {
                      name: trans("Completed Forms"),
                      data: backlogCompletedData,
                    },
                  ]}
                  type="area"
                  height={300}
                />
              </Card>
            </Tooltip>
          </Col>
        )}

        {/* Performance by Location - Enhanced with ApexCharts */}
        <Col span={12}>
          <Tooltip
            title={trans("Compares performance across different locations")}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "8px" }}
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600" }}>
                      {trans("Performance by Location")}
                    </span>
                  </div>
                  <Button
                    type="text"
                    icon={
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/Light/expand.svg`}
                        alt="expend"
                      />
                    }
                    onClick={() => setExpandedChart("location")}
                    style={{
                      color: colorScheme.primary,
                      padding: "4px 8px",
                      border: "1px solid #EBECF0",
                    }}
                    size="small"
                  />
                </div>
              }
            >
              {locationData.length > 0 ? (
                <ReactApexChart
                  options={locationBarOptions}
                  series={[
                    {
                      name: trans("Total Forms"),
                      data: locationData.map((item) => item.totalForms),
                    },
                    {
                      name: trans("Completed"),
                      data: locationData.map((item) => item.completed),
                    },
                    {
                      name: trans("Pending"),
                      data: locationData.map((item) => item.pending),
                    },
                  ]}
                  type="bar"
                  height={300}
                />
              ) : (
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    color: colorScheme.neutral,
                  }}
                >
                  <EnvironmentOutlined
                    style={{
                      fontSize: "32px",
                      marginBottom: "12px",
                      opacity: 0.3,
                    }}
                  />
                  <Text style={{ color: colorScheme.neutral }}>
                    {trans("No location data available")}
                  </Text>
                </div>
              )}
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Secondary Charts Row */}
      <Row gutter={[16, 16]}>
        {/* Top Technicians */}
        <Col span={isExpanded ? 12 : 24}>
          <Tooltip
            title={trans(
              "Highlights employees with the best task completion rates"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {trans("Top Performers")}
                  </span>
                </div>
              }
            >
              <div style={{ height: "300px", overflowY: "auto" }}>
                {technicianPerformance.length > 0 ? (
                  technicianPerformance.slice(0, 5).map((tech, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 16px",
                        marginBottom: "12px",
                        background: `linear-gradient(135deg, ${colorScheme.surface} 0%, ${colorScheme.light} 100%)`,
                        borderRadius: "10px",
                        border:
                          i === 0
                            ? `2px solid ${colorScheme.primary}`
                            : `2px solid ${colorScheme.border}20`,
                        transition: "all 0.3s ease",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = `0 6px 20px ${
                          i === 0 ? colorScheme.primary : colorScheme.neutral
                        }25`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {/* Progress background */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${tech.completionRate}%`,
                          background: `linear-gradient(90deg, ${
                            i === 0 ? colorScheme.primary : colorScheme.neutral
                          }08 0%, ${
                            i === 0 ? colorScheme.primary : colorScheme.neutral
                          }15 100%)`,
                          borderRadius: "8px",
                          transition: "width 0.8s ease",
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background:
                              i === 0
                                ? colorScheme.primary
                                : colorScheme.neutral,
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginInlineEnd: "12px",
                            fontSize: "14px",
                            fontWeight: "600",
                            boxShadow: `0 2px 8px ${
                              i === 0
                                ? colorScheme.primary
                                : colorScheme.neutral
                            }20`,
                          }}
                        >
                          {i + 1}
                        </div>
                        <div>
                          <Text
                            style={{
                              fontWeight: i === 0 ? "600" : "500",
                              fontSize: "13px",
                              color: colorScheme.dark,
                              display: "block",
                            }}
                          >
                            {tech.technicianName}
                          </Text>
                          <Text
                            style={{
                              fontSize: "11px",
                              color: colorScheme.neutral,
                            }}
                          >
                            {tech.completedForms}/{tech.totalForms}{" "}
                            {trans("Forms")}
                          </Text>
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "right",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color:
                              parseFloat(tech.completionRate) >= 90
                                ? colorScheme.success
                                : parseFloat(tech.completionRate) >= 70
                                ? colorScheme.warning
                                : colorScheme.danger,
                            display: "block",
                            lineHeight: "1",
                          }}
                        >
                          {tech.completionRate}%
                        </Text>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      flexDirection: "column",
                      color: colorScheme.neutral,
                    }}
                  >
                    <TrophyOutlined
                      style={{
                        fontSize: "32px",
                        marginBottom: "12px",
                        opacity: 0.3,
                      }}
                    />
                    <Text style={{ color: colorScheme.neutral }}>
                      {trans("No technician data available")}
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Task Types Performance */}
        <Col span={isExpanded ? 12 : 24}>
          <Tooltip
            title={trans("Compares submitted and pending forms by task type")}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "600" }}>
                    {trans("Task Types Performance")}
                  </span>
                </div>
              }
            >
              {taskTypeData.length > 0 ? (
                <ReactApexChart
                  options={taskTypeBarOptions}
                  series={[
                    {
                      name: trans("Submitted"),
                      data: taskTypeData.map((item) => item.submitted),
                    },
                    {
                      name: trans("Pending"),
                      data: taskTypeData.map((item) => item.pending),
                    },
                  ]}
                  type="bar"
                  height={300}
                />
              ) : (
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    color: colorScheme.neutral,
                  }}
                >
                  <BarChartOutlined
                    style={{
                      fontSize: "32px",
                      marginBottom: "12px",
                      opacity: 0.3,
                    }}
                  />
                  <Text style={{ color: colorScheme.neutral }}>
                    {trans("No task type data available")}
                  </Text>
                </div>
              )}
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Expanded Chart Modal */}
      <ExpandedChartModal />
      {selectedForm && (
        <AlertPopup
          alert={selectedForm}
          onClose={() => setSelectedForm(null)}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}
    </div>
  );
}
